/**
 * Image Cropping Utilities
 *
 * Provides functions for cropping images using Canvas API
 * Used with react-easy-crop for image upload preprocessing
 */

/**
 * Create cropped image from source image and crop data
 *
 * @param {string} imageSrc - Source image URL (data URL or blob URL)
 * @param {Object} pixelCrop - Crop area in pixels { x, y, width, height }
 * @param {number} rotation - Rotation angle in degrees (default 0)
 * @param {number} maxWidth - Maximum output width (optional, default: no limit)
 * @returns {Promise<Blob>} - Cropped image as Blob
 */
export async function getCroppedImg(imageSrc, pixelCrop, rotation = 0, maxWidth = null) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { alpha: true });

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );

  // Set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // Translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-image.width / 2, -image.height / 2);

  // Draw rotated image
  ctx.drawImage(image, 0, 0);

  // Create a new canvas for the cropped image
  const croppedCanvas = document.createElement('canvas');
  const croppedCtx = croppedCanvas.getContext('2d', { alpha: true });

  if (!croppedCtx) {
    throw new Error('Failed to get cropped canvas context');
  }

  // Calculate output size (maintain aspect ratio if maxWidth is specified)
  let outputWidth = pixelCrop.width;
  let outputHeight = pixelCrop.height;

  if (maxWidth && outputWidth > maxWidth) {
    const scale = maxWidth / outputWidth;
    outputWidth = maxWidth;
    outputHeight = Math.round(outputHeight * scale);
  }

  // Set the size of the cropped canvas (use high quality)
  croppedCanvas.width = outputWidth;
  croppedCanvas.height = outputHeight;

  // Enable image smoothing for better quality
  croppedCtx.imageSmoothingEnabled = true;
  croppedCtx.imageSmoothingQuality = 'high';

  // Draw the cropped image with high quality
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputWidth,
    outputHeight
  );

  // Detect if the original image is PNG (has transparency)
  const isPNG = imageSrc.includes('data:image/png') || imageSrc.toLowerCase().endsWith('.png');

  // Convert canvas to Blob with high quality
  return new Promise((resolve, reject) => {
    if (isPNG) {
      // Use PNG for images with transparency
      croppedCanvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      }, 'image/png');
    } else {
      // Use high-quality JPEG for photos
      croppedCanvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      }, 'image/jpeg', 0.98); // Increased quality to 0.98
    }
  });
}

/**
 * Create an Image element from a URL
 *
 * @param {string} url - Image URL (data URL or blob URL)
 * @returns {Promise<HTMLImageElement>} - Loaded image element
 */
function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // Needed to avoid CORS issues
    image.src = url;
  });
}

/**
 * Calculate the bounding box size of a rotated rectangle
 *
 * @param {number} width - Original width
 * @param {number} height - Original height
 * @param {number} rotation - Rotation angle in degrees
 * @returns {Object} - { width, height } of bounding box
 */
function rotateSize(width, height, rotation) {
  const rotRad = (rotation * Math.PI) / 180;

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * Convert Blob to File object
 *
 * @param {Blob} blob - Blob to convert
 * @param {string} filename - Name for the file
 * @returns {File} - File object
 */
export function blobToFile(blob, filename) {
  return new File([blob], filename, { type: blob.type });
}

/**
 * Read File as Data URL
 *
 * @param {File} file - File to read
 * @returns {Promise<string>} - Data URL string
 */
export function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
